import { isUrlParamEquals, consoleEvents } from '@pb/utils'

import appSelectors from 'App/duck/selectors'
import {
  CHECKOUT_ERROR_CODES,
  CHECKOUT_TRACKING_ERROR_TYPES,
  CHECKOUT_TRACKING_SECTIONS,
} from 'features/checkout/constants'
import {
  fetchContextFail,
  fetchContextStart,
  postPrepareSuccess,
  postPrepareStart,
  postPrepareFail,
  fetchSettingsSuccess,
} from 'features/checkout/duck/actions'
import { operationFetchCities } from 'features/checkout/duck/operations'
import { normalizePrepare, normalizeSettings } from 'features/checkout/duck/normalize'
import { postPrepare, fetchSettings, fetchPaymentModes } from 'features/checkout/requests'
import getErrorData from 'helpers/getErrorData'
import { ThunkResult } from 'redux/types'
import { ApiError } from 'types'
import { onFetchContext } from './fetchContext'
import { trackError } from './track'

const trackPrepareError =
  (code: string): ThunkResult<void> =>
  (dispatch) =>
    dispatch(
      trackError({
        section: CHECKOUT_TRACKING_SECTIONS.general,
        id: code,
        errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
      }),
    )

const operationPrepareError =
  (error: ApiError[], operation: () => void): ThunkResult<Promise<void>> =>
  async (dispatch) => {
    const { code } = getErrorData(error)

    dispatch(trackPrepareError(code))

    if (code === CHECKOUT_ERROR_CODES.duplicatedUID) {
      dispatch(operation())
    } else {
      dispatch(postPrepareFail())
    }
  }

export const operationPostPrepare =
  (): ThunkResult<Promise<void>> => async (dispatch, getState) => {
    dispatch(postPrepareStart())
    const state = getState()
    try {
      if (isUrlParamEquals('prepare', 'false')) {
        // This scenario happends when a user is being redirected back to checkout when a external payment provider have failed
        // and we don't want to create a new session calling the post prepare API!

        dispatch(postPrepareSuccess({}))
      } else {
        const customerType = appSelectors.getCustomerType(state) || null
        const provinces = appSelectors.getProvinces(state)
        const [prepareResponse, checkoutSettings] = await Promise.all([
          postPrepare(customerType),
          fetchSettings(),
        ])
        const preparePayment = prepareResponse?.context?.payment
        const isPaymentValidated =
          preparePayment?.validatedPayment &&
          preparePayment?.validatedBilling &&
          !preparePayment?.paymentUserInputNeeded
        const normalizedPrepare = normalizePrepare(prepareResponse)
        const normalizedSettings = normalizeSettings(checkoutSettings)
        dispatch(fetchSettingsSuccess(normalizedSettings))

        if (isPaymentValidated) {
          try {
            const { paymentModeList } = await fetchPaymentModes()
            if (prepareResponse?.context) {
              prepareResponse.context.payment.paymentModeList = paymentModeList.map((mode) => ({
                ...normalizedSettings?.paymentSettings?.paymentModes?.[mode.code],
                ...mode,
              }))
            }
          } catch (err) {
            consoleEvents.error('Failed to fetch payment options', err)
          }
        }
        try {
          dispatch(fetchContextStart())
          const { context } = prepareResponse
          dispatch(onFetchContext(context))

          // Fetch cities when you have single province and it's not even shown (eg: Taiwan)
          if (provinces && Object.keys(provinces).length === 1) {
            const value = Object.values(provinces)[0]
            dispatch(operationFetchCities({ provinces, value }))
          }
        } catch (error) {
          consoleEvents.error('Failed to fetch context', error)
          dispatch(fetchContextFail({}))
        }
        dispatch(postPrepareSuccess(normalizedPrepare))
      }
    } catch (error) {
      dispatch(operationPrepareError(error, operationPostPrepare))
    }
  }
